import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Dropdown, Overlay, Popover } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'video-react/dist/video-react.css';

import { updateFilterPartial } from '../../../queries';
import { APP_ROUTES } from '../../../constants/routes';
import { EventTypeIdentifier } from '../../../enums';
import { useEventPreview, useEventTypes } from '../../../hooks';
import { defaultFilter } from '../../../constants/FleetDefaultFilters';
import { Event } from '../../../interfaces';
import { isSentToProcessor } from '../../../utils';
import { DisabledWithTooltip } from '../../components/DisabledWithTooltip';
import { InvisibleButton } from '../../event_details/components';
import { AddPlateModal } from '../../whitelist/AddPlateModal';
import { Permission } from '../adminOnly';
import { ButtonTag } from '../eventTypeTag';
import {
  LicencePlateButtonOverlay,
  LicencePlateButtonState,
  LicencePlateInvisibleButtonWrapper,
  LicencePlateWrapper,
  StyledDropdownItem,
} from './atoms';
import { LicensePlateEditModal } from './licensePlateModal';
import styled from 'styled-components';

interface BaseProps {
  onClick?: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  hideWhitelistRibbon?: boolean;
  licensePlateUpdate?: (newEventFields: Partial<Event>) => void;
  keyboardShortcutEnabled?: boolean;
  isLicenscePlateException?: boolean;
  isBigger?: boolean;
}

interface EventProps extends BaseProps {
  event: Event;
  licensePlate?: never;
  state?: never;
}

interface TextProps extends BaseProps {
  event?: never;
  licensePlate: string;
  state: string;
}

type Props = EventProps | TextProps;

// This takes either first or second set of props
const getStateFromProps = (props: Props): string => {
  if (props.event?.event_type_identifier === EventTypeIdentifier.ROADWORKS) {
    return '';
  }

  let state = '';
  if ('event' in props) {
    state = (props as Props).event?.license_plate_region ?? '';
  } else if ('state' in props) {
    state = (props as TextProps).state ?? '';
  }

  const iof = state.indexOf('-');
  if (iof > -1) {
    return state.substring(iof + 1).toUpperCase();
  }
  return state.toUpperCase();
};

export function LicencePlateButton(props: Props) {
  const [state, setState] = useState(getStateFromProps(props));
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showAddPermittedModal, setShowAddPermittedModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<Partial<Event> | null>(null);
  const target = useRef(null);
  const history = useHistory();
  const { closeEventModal } = useEventPreview();

  const { onClick, className, hideWhitelistRibbon, keyboardShortcutEnabled, isLicenscePlateException } =
    props as BaseProps;
  const { event } = props as Props;
  const { licensePlate } = props as TextProps;

  const permittedTooltip =
    props.event?.event_type_identifier === EventTypeIdentifier.ROADWORKS ? 'Work permit' : 'Permitted plate';

  const size = props.size ?? 'sm';

  const allowedList = !hideWhitelistRibbon && event?.whitelist;

  const eventTypes = useEventTypes();

  const availableTypes = eventTypes
    ?.filter((type) => {
      return !type.identifier.includes('_MATCH');
    })
    .map((t) => t.identifier);

  useEffect(() => setState(getStateFromProps(props)), [props.event, props.state]);

  useEffect(() => {
    // Add click outside listener
    window.addEventListener('click', clickOutside);

    // Add escape key listener to close menu
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showPopup) {
        setShowPopup(false);
      }
    };
    window.addEventListener('keydown', handleEscKey);

    return () => {
      window.removeEventListener('click', clickOutside);
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [showPopup]); // Add showPopup as dependency to correctly remove listener

  const click = (evt: SyntheticEvent) => {
    setShowPopup(!showPopup);

    evt.stopPropagation();
    evt.preventDefault();
  };

  const clickOutside = () => setShowPopup(false);

  const filterToThisCar = () => {
    setShowPopup(false);
    updateFilterPartial({ license_plates: [getLicensePlate()] });
    closeEventModal();
    history.push(APP_ROUTES.portalList);
  };

  const viewAllEventsForThisCar = () => {
    setShowPopup(false);
    updateFilterPartial({
      ...defaultFilter,
      license_plates: [getLicensePlate()],
      selectedFilters: [],
    });
    closeEventModal();
    // Allow some time for the filters to propagate
    setTimeout(() => {
      history.push(APP_ROUTES.portalList);
    }, 250);
  };

  const viewAllSingleObservations = () => {
    const filterForObservations = {
      ...defaultFilter,
      license_plates: [getLicensePlate()],
      selectedFilters: availableTypes,
    };
    setShowPopup(false);
    updateFilterPartial({ ...filterForObservations });

    closeEventModal();
    // Allow some time for the filters to propagate
    setTimeout(() => {
      history.push(APP_ROUTES.portalList);
    }, 250);
  };

  const disabled = props.event && isSentToProcessor(props.event);

  const openLPREditModal = (evt: SyntheticEvent) => {
    evt.stopPropagation();
    evt.preventDefault();
    if (disabled) {
      toast.info('This event has been sent to processor and cannot be edited');
    } else {
      setShowPopup(false);
      if (event) {
        setShowEditModal(event);
      }
    }
  };

  const closeLPEditModal = () => {
    setShowEditModal(null);
    setShowPopup(false);
  };

  const getLicensePlate = () => {
    if (props.event) {
      return props.event.license_plate;
    }
    return licensePlate;
  };

  const addToPermittedList = () => {
    setShowAddPermittedModal(true);
  };

  const hideOverlays = () => {
    setShowAddPermittedModal(false);
    setShowPopup(false);
  };

  const copyLP = () => {
    navigator.clipboard.writeText(getLicensePlate()).then(() => {
      toast.info('Copied!');
    });
  };

  return (
    <>
      <LicencePlateWrapper
        onClick={click}
        className={`hdn-license-plate-button ${className}`}
        data-test={props['data-test']}
        isBigger={props.isBigger}
      >
        <span ref={target}>
          <ButtonTag
            className={`btn btn-${size} hdn-text--ellipsis`}
            onClick={onClick}
            allowedListed={allowedList}
            tooltip={allowedList ? `${permittedTooltip} (${getLicensePlate()})` : getLicensePlate()}
            aria-controls="popover-lp"
            aria-expanded={showPopup}
            data-test={allowedList ? 'permitted' : undefined}
          >
            {getLicensePlate()}
            {state && (
              <LicencePlateButtonState data-test="license-plate-state">
                {state.charAt(0)}
                <br />
                {state.charAt(1)}
              </LicencePlateButtonState>
            )}
          </ButtonTag>
        </span>

        {keyboardShortcutEnabled && (
          <LicencePlateInvisibleButtonWrapper aria-hidden tabIndex={-1}>
            <InvisibleButton
              onClick={openLPREditModal}
              data-accessKey="KeyL"
              visibilityHidden
              left="-20px"
              top="10px"
            />
          </LicencePlateInvisibleButtonWrapper>
        )}

        <Overlay target={target.current} show={showPopup} placement="top">
          <Popover id="popover-lp" role={undefined}>
            {showPopup && (
              <LicencePlateButtonOverlay>
                {props.event && (
                  <>
                    <StyledDropdownItem onClick={copyLP}>Copy to clipboard</StyledDropdownItem>
                    <Dropdown.Divider />
                  </>
                )}

                <Permission permission="events:read:list">
                  <StyledDropdownItem onClick={filterToThisCar}>Filter results to this car</StyledDropdownItem>
                  <Dropdown.Divider />
                  <StyledDropdownItem onClick={viewAllEventsForThisCar}>
                    View all events for this car
                  </StyledDropdownItem>

                  {isLicenscePlateException && (
                    <>
                      <Dropdown.Divider />
                      <StyledDropdownItem onClick={viewAllSingleObservations}>
                        View all single observations for this car
                      </StyledDropdownItem>
                    </>
                  )}
                </Permission>

                {!isLicenscePlateException && (
                  <Permission permission="fleets:write:whitelist_plates">
                    <Dropdown.Divider />
                    <StyledDropdownItem onClick={addToPermittedList}>Add to permitted list</StyledDropdownItem>
                  </Permission>
                )}

                {props.licensePlateUpdate && props.event && (
                  <Permission permission="events:write:license_plate">
                    <Dropdown.Divider />
                    <DisabledWithTooltip disabled={disabled}>
                      <StyledDropdownItem disabled={disabled} onClick={openLPREditModal}>
                        Edit license plate
                      </StyledDropdownItem>
                    </DisabledWithTooltip>
                  </Permission>
                )}
              </LicencePlateButtonOverlay>
            )}
          </Popover>
        </Overlay>
      </LicencePlateWrapper>

      {showEditModal && props.event && (
        <LicensePlateEditModal
          event={props.event}
          onClose={closeLPEditModal}
          licensePlateUpdate={props.licensePlateUpdate}
        />
      )}

      {showAddPermittedModal && (
        <AddPlateModal
          toggle={hideOverlays}
          plate={{
            license_plate: getLicensePlate(),
            categorization_code: '',
            state_code: props.event?.metadata?.region,
            event_type_identifiers: [EventTypeIdentifier.BUS_LANE],
          }}
        />
      )}
    </>
  );
}
